import { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { DataConfig } from '../../../types/bi';
import { useCreateAlert } from '../hooks/useDashboards';
import styles from './AlertDialog.module.css';

const CONDITIONS = [
  { value: 'gt', label: 'Greater than' },
  { value: 'lt', label: 'Less than' },
  { value: 'gte', label: 'Greater or equal' },
  { value: 'lte', label: 'Less or equal' },
  { value: 'eq', label: 'Equals' },
  { value: 'change_pct', label: 'Changes by %' },
] as const;

const INTERVALS = [
  { value: '15m', label: 'Every 15 minutes' },
  { value: '1h', label: 'Every hour' },
  { value: '6h', label: 'Every 6 hours' },
  { value: '24h', label: 'Daily' },
] as const;

const CHANNELS = [
  { value: 'in_app', label: 'In-app notification' },
  { value: 'email', label: 'Email' },
  { value: 'both', label: 'Both' },
] as const;

interface AlertDialogProps {
  dataConfig: DataConfig;
  widgetTitle: string;
  onClose: () => void;
}

export default function AlertDialog({ dataConfig, widgetTitle, onClose }: AlertDialogProps) {
  const createMut = useCreateAlert();

  const measures = dataConfig.measures ?? [];
  const primaryMeasure = measures[0]?.alias ?? 'value';

  const [name, setName] = useState(`Alert: ${widgetTitle}`);
  const [condition, setCondition] = useState<string>('gt');
  const [threshold, setThreshold] = useState<string>('');
  const [channel, setChannel] = useState<string>('in_app');
  const [interval, setInterval] = useState<string>('1h');
  const [error, setError] = useState('');

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const t = Number(threshold);
    if (isNaN(t)) {
      setError('Threshold must be a number');
      return;
    }
    if (measures.length === 0) {
      setError('Widget must have at least one measure');
      return;
    }

    createMut.mutate(
      {
        name: name.trim(),
        data_config: dataConfig,
        condition,
        threshold: t,
        notify_channel: channel,
        check_interval: interval,
      },
      {
        onSuccess: () => onClose(),
        onError: (e: Error) => setError(e.message),
      },
    );
  }, [name, threshold, condition, channel, interval, dataConfig, measures, createMut, onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Create Data Alert</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.field}>
            <span className={styles.label}>Alert name</span>
            <input
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Metric</span>
            <input className={styles.input} value={primaryMeasure} readOnly />
            <span className={styles.hint}>
              Uses the first measure from this widget&apos;s query
            </span>
          </div>

          <div className={styles.conditionRow}>
            <div className={styles.field}>
              <span className={styles.label}>Condition</span>
              <select
                className={styles.select}
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
              >
                {CONDITIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Threshold</span>
              <input
                className={styles.input}
                type="number"
                placeholder="100"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Notification channel</span>
            <select
              className={styles.select}
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            >
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Check interval</span>
            <select
              className={styles.select}
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
            >
              {INTERVALS.map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={createMut.isPending}>
            {createMut.isPending ? 'Creating…' : 'Create alert'}
          </button>
        </div>
      </div>
    </div>
  );
}
